import { Component } from '../lib/ComponentBase';
import { simpleComponent } from '../lib/ComponentHelpers';
import type { MoveProvider, DisplayProvider, DisplayInfo, PathfindingNode, WorldState } from '../types/Components';
import type { Entity } from '../types/Entity';
import { ActionType, ActionMode } from '../lib/Action';
import Action from '../lib/Action';
import HexPosition from './HexPosition';

/**
 * An ability that allows an entity to move.
 * It bids with a low priority to propose a MOVE outcome.
 */
class MoveForward extends Component implements MoveProvider, DisplayProvider {
  static supportedModes = [ActionMode.STANDARD];
  priority: number;

  constructor(data: { priority?: number } = {}) {
    super();
    this.priority = data.priority ?? 1;
  }

  getDisplayInfo(): DisplayInfo {
    return { icon: 'game-icons:walking-boot', label: 'Move Forward', description: 'Moves one hex in the direction faced.' };
  }

  getPossibleMoves(fromNode: PathfindingNode, worldState: { walkableCoords: Set<string> }): PathfindingNode[] {
    const { coords, bearing } = fromNode;
    const { walkableCoords } = worldState;
    const moves: PathfindingNode[] = [];

    const nextCoords = coords.neighbor(bearing);
    if (walkableCoords.has(nextCoords.key)) {
      moves.push({ coords: nextCoords, bearing: bearing, cost: 1 });
    }
    return moves;
  }

  getPossiblePlayerActions(entity: Entity, mode: typeof ActionMode[keyof typeof ActionMode], worldState: WorldState): Map<string, Action> {
    const actions = new Map<string, Action>();
    if (mode !== ActionMode.STANDARD) return actions;

    const selfPosition = entity.getComponent(HexPosition);
    if (!selfPosition) return actions;

    const fromNode = { coords: selfPosition.coords, bearing: selfPosition.bearing };
    const possibleMoves = this.getPossibleMoves(fromNode, worldState);

    for (const move of possibleMoves) {
      actions.set(
        move.coords.key,
        new Action(entity, ActionType.MOVE, { coords: move.coords })
      );
    }

    return actions;
  }
}

export default simpleComponent(MoveForward);
